import React, { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, CalendarDays, Mic, MessageSquare, Grid3x3 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";

/**
 * Fixed bottom navigation — identical for every role.
 *
 * Slots (left → right):
 *   1. Dashboard → switches the mobile home tab back to "Overview"
 *   2. Jobs      → /jobs (full job list)
 *   3. Guru Mic  → tap = start a Guru voice session, long-press = same
 *                  (dispatches `guru:voice-start`; VoiceAgent listens)
 *   4. Messages  → /messages
 *   5. More      → opens the MobileAppDrawer (Calendar, Pipeline, Pricebook,
 *                  Staff, Settings, Time Clock, Location Share, etc. all
 *                  live there now — see MobileAppDrawer.jsx)
 *
 * `onSelectTab` is optional — when the user is already on `/mobile`, we call
 * it instead of navigating so the top-tab state is updated in place. From any
 * other route we navigate to `/mobile?tab=...` so the parent picks it up.
 */

const ACTIVE = "#0063F7";
const INACTIVE = "#6B7280";

const NavItem = ({ id, icon: Icon, label, active, onClick, testid, badge }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-label={label}
    className="cc-nochrome flex-1 min-w-0 h-full flex flex-col items-center justify-center gap-0.5 !p-0"
  >
    <div className="relative">
      <Icon className="h-[26px] w-[26px] shrink-0" color={active ? ACTIVE : INACTIVE} strokeWidth={2.2} />
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-[3px] rounded-full bg-red-600 text-white text-[9.5px] font-bold flex items-center justify-center leading-none"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </div>
    <span className="text-[10.5px] font-semibold leading-none tracking-tight truncate max-w-full" style={{ color: active ? ACTIVE : INACTIVE }}>{label}</span>
    <span
      className="mt-0.5 h-[2.5px] w-5 rounded-full transition-opacity"
      style={{ backgroundColor: ACTIVE, opacity: active ? 1 : 0 }}
      aria-hidden="true"
    />
  </button>
);

export default function MobileBottomNav({ activeTab, onSelectTab }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const chat = useChat();
  const unreadCount = chat?.unread?.total || 0;
  const isTech = user?.role === "technician";
  const onMobileHome = location.pathname === "/mobile";

  const setTab = (key) => {
    if (onMobileHome && onSelectTab) onSelectTab(key);
    else navigate(`/mobile?tab=${key}`);
  };

  // Technicians land on their /mobile Home; everyone else lands on /dashboard
  // — both render the exact same MobileHome component.
  const goDashboard = () => {
    if (isTech) setTab("overview");
    else navigate("/dashboard");
  };
  const goJobs = () => navigate("/jobs");
  const openMore = () => window.dispatchEvent(new CustomEvent("open-mobile-drawer"));

  // Long-press vs. tap discrimination for the Guru mic.
  const pressTimer = useRef(null);
  const longPressFired = useRef(false);
  const startPress = () => {
    longPressFired.current = false;
    pressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      try { navigator.vibrate && navigator.vibrate(20); } catch (_err) { /* vibrate is best-effort */ }
      window.dispatchEvent(new CustomEvent("guru:voice-start"));
    }, 450);
  };
  const cancelPress = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };
  const onMicClick = () => {
    // Tap AND long-press both start Guru Assistant — the long-press just
    // provides earlier haptic feedback for held touches.
    if (longPressFired.current) return; // session already started via long-press
    window.dispatchEvent(new CustomEvent("guru:voice-start"));
  };

  const item = (id, icon, label, testid, onClick, badge) => (
    <NavItem id={id} icon={icon} label={label} active={activeTab === id} testid={testid} onClick={onClick} badge={badge} />
  );

  return (
    <div
      data-testid="mobile-bottom-nav"
      className="fixed bottom-0 inset-x-0 z-50"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <nav
        role="navigation"
        aria-label="Primary"
        className="min-h-[65px] flex items-stretch pt-2"
        style={{
          backgroundColor: "#FFFFFF",
          borderTop: "1px solid #E5E7EB",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)",
        }}
      >
        {item("overview", Home,         "Dashboard", "bn-dashboard", goDashboard)}
        {item("jobs",     CalendarDays, "Jobs",      "bn-jobs",      goJobs)}

        {/* Fixed-width spacer — just wide enough to clear the mic's
            footprint (60px button + a little breathing room), so it no
            longer eats an equal flex-1 share and starves the 4 real
            items of width now that mobile text renders larger. */}
        <div className="w-16 shrink-0" aria-hidden="true" />

        {item("messages", MessageSquare, "Messages", "bn-messages", () => navigate("/messages"), unreadCount)}
        {item("more",     Grid3x3,       "More",     "bn-more",     openMore)}
      </nav>

      {/* Guru mic — rendered as a sibling of <nav>, NOT a descendant, so
          it's never subject to the nav's `overflow-x-hidden` (CSS forces
          overflow-y to compute as `auto`, clipping any element, whenever
          overflow-x is non-visible on the SAME box — see CSS Overflow
          Level 3 §3). Living outside that box lets it rise a full ~20px
          above the bar and sit above all page content, on every page. */}
      <button
        data-testid="bn-guru-mic"
        aria-label="Guru Assistant — voice commands"
        onClick={onMicClick}
        onMouseDown={startPress}
        onMouseUp={cancelPress}
        onMouseLeave={cancelPress}
        onTouchStart={startPress}
        onTouchEnd={cancelPress}
        onTouchCancel={cancelPress}
        className="absolute left-1/2 -translate-x-1/2 top-[-20px] z-10 h-[60px] w-[60px] rounded-full text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform ring-4 ring-white"
        style={{ background: "#0077CC", boxShadow: "0 8px 16px rgba(0,119,204,0.35), 0 0 0 10px rgba(0,119,204,0.12)" }}
      >
        <Mic className="h-[31px] w-[31px] shrink-0" strokeWidth={2.4} style={{ color: "#ffffff" }} />
      </button>
    </div>
  );
}
